import axios from "axios";
import {
  defaultVerifiedUrl,
  emailSenderUrl,
  otpSender,
  smsSenderUrl,
} from "../../constants";
import { hashTheString } from "@verified-network/verified-custody";
import {
  authenticateFirestore,
  getAndSaveToken,
  getUser,
  saveNewUser,
  updatePassword,
} from "../firebase";
import { ethers } from "ethers";
import { chainDetails } from "../../config";

export const convertFromTimeStamp = (timeStamp) => {
  return new Date(timeStamp).toLocaleString("default", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

export const sendOTPVerification = async (
  creatorUniqueId,
  channel,
  recipient,
) => {
  return await axios({
    url: otpSender,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    data: {
      action: "generate",
      uniqueId: creatorUniqueId,
    },
  })
    .then(async (res) => {
      if (res?.data?.token) {
        return await axios({
          url: otpSender,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${res?.data?.token}`,
          },
          data: {
            action: "start",
            recipient,
            channel,
            uniqueId: creatorUniqueId,
          },
        })
          .then((_res) => {
            if (_res?.data?.messageId) {
              return true;
            } else {
              return false;
            }
          })
          .catch((_err) => {
            console.error("send otp failed?: ", _err?.message);
            return false;
          });
      } else {
        return false;
      }
    })
    .catch((err) => {
      console.error("generate jwt token failed?: ", err?.message);
      return false;
    });
};

export const checkOTPVerification = async (
  creatorUniqueId,
  recipient,
  code,
) => {
  return await axios({
    url: otpSender,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    data: {
      action: "generate",
      uniqueId: creatorUniqueId,
    },
  })
    .then(async (res) => {
      if (res?.data?.token) {
        return await axios({
          url: otpSender,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${res?.data?.token}`,
          },
          data: {
            action: "verify",
            recipient,
            code,
            uniqueId: creatorUniqueId,
          },
        })
          .then((res) => {
            if (res?.data?.verified) {
              return true;
            } else {
              return false;
            }
          })
          .catch((err) => {
            console.error("check otp failed?: ", err?.message);
            return false;
          });
      } else {
        return false;
      }
    })
    .catch((err) => {
      console.error("generate jwt token failed?: ", err);
      return false;
    });
};

export const sendCoSignerInvitation = async (
  channel,
  cosignerId,
  vaultId,
  vaultPinHashed,
) => {
  const userId = hashTheString(vaultId);
  const authenticated = await authenticateFirestore(userId, vaultPinHashed);
  if (authenticated) {
    return await axios({
      url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
      method: "POST",
      data: {
        uniqueId: authenticated,
        type: "generate",
      },
    })
      .then(async (res) => {
        if (res?.data?.token) {
          const userId = hashTheString(cosignerId);
          return await axios({
            url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
            method: "POST",

            headers: {
              Authorization: `Bearer ${res?.data?.token}`,
            },
            data: {
              recipient: cosignerId,
              type: "invitation_received",
              subject: "Verified Wallet Co-signer Invitation",
              origin: defaultVerifiedUrl,
              vaultId: vaultId,
              vaultPinHashed: "0x",
              coSignerId: cosignerId,
              notification: {
                userId: userId,
                title: "🤝 New Co-signer Invitation Received!",
                body: `You’ve been invited to be a co-signer by ${vaultId}. Review the invitation today.`,
                data: {
                  event: "invitation_received",
                  message: `You’ve been invited to be a co-signer by ${vaultId}. Review the invitation today.`,
                },
              },
            },
          })
            .then((_res) => {
              if (_res?.data) {
                return true;
              } else {
                return false;
              }
            })
            .catch((err) => {
              if (process.env.REACT_APP_NODE_ENV !== "production") {
                console.error(
                  "coSinerInvitation failed with error: ",
                  err?.message,
                );
              }

              return false;
            });
        } else {
          return false;
        }
      })
      .catch((err) => {
        if (process.env.REACT_APP_NODE_ENV !== "production") {
          console.error("coSinerInvitation failed with error: ", err?.message);
        }

        return false;
      });
  } else {
    return false;
  }
};

export const sendCreatorConfirmation = async (
  channel,
  creatorId,
  coSignersList,
  requiredSigners,
  vaultPinHashed,
  isRemoved,
) => {
  const userId = hashTheString(creatorId);
  const authenticated = await authenticateFirestore(userId, vaultPinHashed);
  if (authenticated) {
    await getAndSaveToken(userId);
    await saveNewUser(
      userId,
      creatorId,
      channel,
      coSignersList?.map((cng) =>
        cng?.email?.length > 0 ? cng?.email : cng?.phoneNumber,
      ),
      vaultPinHashed,
    );
    return await axios({
      url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
      method: "POST",

      data: {
        uniqueId: authenticated,
        type: "generate",
      },
    })
      .then(async (res) => {
        if (res?.data?.token) {
          return await axios({
            url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
            method: "POST",

            headers: {
              Authorization: `Bearer ${res?.data?.token}`,
            },
            data: {
              recipient: creatorId,
              type: "invitation_sent",
              subject: "Verified Wallet Co-signers",
              coSignersList: coSignersList,
              requiredSigners: String(requiredSigners),
              origin: defaultVerifiedUrl,
              notification: {
                userId: userId,
                title: isRemoved
                  ? "🎉 Cosigner Removed"
                  : "🎉 Account Created!",
                body: isRemoved
                  ? `You have successfully removed ${coSignersList?.length} Co-signer(s) from your account. Check your dashboard for new co-signer's list`
                  : `Your account is now live. You’ve invited ${
                      coSignersList?.length
                    } co‑signers to secure you account. Your signing rule is: ${requiredSigners} out of ${
                      coSignersList?.length + 1
                    }`,
                data: {
                  event: isRemoved ? "cosigner_removed" : "account_created",
                  coSignersCount: `${coSignersList?.length}`,
                  sidningRule: isRemoved
                    ? `${requiredSigners} out of ${coSignersList?.length + 1}`
                    : "",
                  message: isRemoved
                    ? `You have successfully removed ${coSignersList?.length} Co-signer(s) from your account. Check your dashboard for new co-signer's list`
                    : `Your account is now live. You’ve invited ${
                        coSignersList?.length
                      } co‑signers to secure you account. Your signing rule is: ${requiredSigners} out of ${
                        coSignersList?.length + 1
                      }`,
                },
              },
            },
          })
            .then((_res) => {
              if (_res?.data) {
                return true;
              } else {
                return false;
              }
            })
            .catch((err) => {
              if (process.env.REACT_APP_NODE_ENV !== "production") {
                console.error(
                  "coSinerInvitation failed with error: ",
                  err?.message,
                );
              }

              return false;
            });
        } else {
          return false;
        }
      })
      .catch((err) => {
        if (process.env.REACT_APP_NODE_ENV !== "production") {
          console.error("coSinerInvitation failed with error: ", err?.message);
        }

        return false;
      });
  } else {
    return false;
  }
};

export const sendCreatorAuthChanged = async (
  channel,
  creatorId,
  vaultPinHashed,
  oldVaultPinHashed,
  timeStamp,
) => {
  const userId = hashTheString(creatorId);
  const authenticated = await authenticateFirestore(userId, oldVaultPinHashed);
  if (authenticated) {
    await getAndSaveToken(userId);
    await updatePassword(userId, vaultPinHashed);
    return await axios({
      url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
      method: "POST",

      data: {
        uniqueId: authenticated,
        type: "generate",
      },
    })
      .then(async (res) => {
        if (res?.data?.token) {
          return await axios({
            url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
            method: "POST",
            headers: {
              Authorization: `Bearer ${res?.data?.token}`,
            },
            data: {
              recipient: creatorId,
              type: "auth_changed",
              subject: "Verified Wallet Authentication",
              timeStamp: timeStamp,
              origin: defaultVerifiedUrl,
              notification: {
                userId: userId,
                title: "🎉 Authentication Changed",
                body: `You have successfully changed your account authentication by ${convertFromTimeStamp(
                  Number(timeStamp),
                )}. Use new Passkey/Pin to access your account.`,

                data: {
                  event: "auth_changed",

                  message: `You have successfully changed your account authentication by ${convertFromTimeStamp(
                    Number(timeStamp),
                  )}. Use new Passkey/Pin to access your account.`,
                },
              },
            },
          })
            .then((_res) => {
              if (_res?.data) {
                return true;
              } else {
                return false;
              }
            })
            .catch((err) => {
              if (process.env.REACT_APP_NODE_ENV !== "production") {
                console.error(
                  "coSinerInvitation failed with error: ",
                  err?.message,
                );
              }

              return false;
            });
        } else {
          return false;
        }
      })
      .catch((err) => {
        if (process.env.REACT_APP_NODE_ENV !== "production") {
          console.error("coSinerInvitation failed with error: ", err?.message);
        }

        return false;
      });
  } else {
    return false;
  }
};

export const sendCreatorAcceptance = async (
  channel,
  creatorId,
  coSignerId,
  vaultPinHashed,
) => {
  //try authenticate firestore with creator pin hash.
  const userId = hashTheString(coSignerId);
  const authenticated = await authenticateFirestore(userId, vaultPinHashed);
  if (authenticated) {
    await getAndSaveToken(userId);
    return await axios({
      url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
      method: "POST",

      data: {
        uniqueId: authenticated,
        type: "generate",
      },
    })
      .then(async (res) => {
        if (res?.data?.token) {
          return await axios({
            url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
            method: "POST",

            headers: {
              Authorization: `Bearer ${res?.data?.token}`,
            },
            data: {
              recipient: creatorId,
              type: "invitation_accepted",
              subject: "Verified Wallet Co-signers",
              coSignerId: coSignerId,
              vaultId: coSignerId,
              origin: defaultVerifiedUrl,
              notification: {
                userId: userId,
                title: "✅ Co-signer Invitation Accepted!",
                body: `Great news! Your co-signer invitation has been accepted by ${coSignerId}. Your account security is now stronger.`,
                data: {
                  event: "invitation_accepted",

                  message: `Great news! Your co-signer invitation has been accepted by ${coSignerId}. Your account security is now stronger.`,
                },
              },
            },
          })
            .then((_res) => {
              if (_res?.data) {
                return true;
              } else {
                return false;
              }
            })
            .catch((err) => {
              if (process.env.REACT_APP_NODE_ENV !== "production") {
                console.error(
                  "coSinerInvitation failed with error: ",
                  err?.message,
                );
              }

              return false;
            });
        } else {
          return false;
        }
      })
      .catch((err) => {
        if (process.env.REACT_APP_NODE_ENV !== "production") {
          console.error("coSinerInvitation failed with error: ", err?.message);
        }

        return false;
      });
  }
};

export const sendCreatorRejection = async (
  channel,
  creatorId,
  coSignerId,
  vaultPinHashed,
) => {
  const userId = hashTheString(coSignerId);
  const authenticated = await authenticateFirestore(userId, vaultPinHashed);
  if (authenticated) {
    await getAndSaveToken(userId);
    return await axios({
      url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
      method: "POST",

      data: {
        uniqueId: authenticated,
        type: "generate",
      },
    })
      .then(async (res) => {
        if (res?.data?.token) {
          return await axios({
            url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
            method: "POST",
            headers: {
              Authorization: `Bearer ${res?.data?.token}`,
            },
            data: {
              recipient: creatorId,
              type: "invitation_rejected",
              subject: "Verified Wallet Co-signers",
              coSignerId: coSignerId,
              vaultId: coSignerId,
              origin: defaultVerifiedUrl,
              notification: {
                userId: userId,
                title: "❌ Co-signer Invitation Rejected",
                body: `Unfortunately, your co-signer invitation was declined by ${coSignerId}. You can invite someone else to help strengthen your account security.`,
                data: {
                  event: "invitation_rejected",

                  message: `Unfortunately, your co-signer invitation was declined by ${coSignerId}. You can invite someone else to help strengthen your account security.`,
                },
              },
            },
          })
            .then((_res) => {
              if (_res?.data) {
                return true;
              } else {
                return false;
              }
            })
            .catch((err) => {
              if (process.env.REACT_APP_NODE_ENV !== "production") {
                console.error(
                  "coSignerInvitation failed with error: ",
                  err?.message,
                );
              }

              return false;
            });
        } else {
          return false;
        }
      })
      .catch((err) => {
        if (process.env.REACT_APP_NODE_ENV !== "production") {
          console.error("coSinerInvitation failed with error: ", err?.message);
        }

        return false;
      });
  }
};

export const sendCreatorInitiation = async (
  channel,
  creatorId,
  vaultPinHashed,
  txId,
  requiredSigners,
) => {
  const userId = hashTheString(creatorId);
  const authenticated = await authenticateFirestore(userId, vaultPinHashed);
  if (authenticated) {
    await getAndSaveToken(userId);
    let user;
    user = await getUser(userId);
    user = await getUser(userId);
    if (user?.cosigners?.length > 0) {
      const alertCosignersPromises = user?.cosigners.map(async (cng) => {
        const _authenticated = await authenticateFirestore(
          userId,
          vaultPinHashed,
        );
        if (_authenticated) {
          return await axios({
            url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
            method: "POST",

            data: {
              uniqueId: _authenticated,
              type: "generate",
            },
          }).then(async (res) => {
            if (res?.data?.token) {
              await axios({
                url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
                method: "POST",
                headers: {
                  Authorization: `Bearer ${res?.data?.token}`,
                },
                data: {
                  recipient: cng,
                  type: "recovery_initiated_cng",
                  subject: "Verified Wallet Approval",
                  vaultId: creatorId,
                  vaultPinHashed: vaultPinHashed,
                  txId: String(txId),
                  origin: defaultVerifiedUrl,
                  requiredSigners: String(requiredSigners),
                  notification: {
                    userId: hashTheString(cng),
                    title: "🔐 Account Recovery Approval!",
                    body: `${creatorId} account recovery has started,  but it’s not complete yet!.
          You are required to help sign the account recovery to complete it.`,
                    data: {
                      event: "recovery_initiated",
                      message: `${creatorId} account recovery has started,  but it’s not complete yet!.
          You are required to help sign the account recovery to complete it.`,
                    },
                  },
                },
              });
            }
          });
        }
      });
      await Promise.all(alertCosignersPromises);
    }

    if (
      (user?.cosigners?.length === 0 && user?.cosignersIv?.length > 0) ||
      (user?.cosigners?.length < Number(requiredSigners) - 1 &&
        user?.cosignersIv?.length > 0)
    ) {
      const alertCosignersInvPromises = user?.cosignersIv.map(async (cng) => {
        if (cng && !user?.cosigners?.includes(cng)) {
          const _authenticated = await authenticateFirestore(
            userId,
            vaultPinHashed,
          );
          if (_authenticated) {
            return await axios({
              url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
              method: "POST",

              data: {
                uniqueId: _authenticated,
                type: "generate",
              },
            }).then(async (res) => {
              if (res?.data?.token) {
                await axios({
                  url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
                  method: "POST",
                  headers: {
                    Authorization: `Bearer ${res?.data?.token}`,
                  },
                  data: {
                    recipient: cng,
                    type: "recovery_initiated_cng",
                    subject: "Verified Wallet Approval",
                    vaultId: creatorId,
                    vaultPinHashed: vaultPinHashed,
                    txId: String(txId),
                    origin: defaultVerifiedUrl,
                    requiredSigners: "0",
                    notification: {
                      userId: hashTheString(cng),
                      title: "🔐 Account Recovery Approval!",
                      body: `${creatorId} account recovery has started,  but it’s not complete yet!.
          You are required to help sign the account recovery to complete it.

          Please Note: You are yet to accept the cosigner invitation, You have to accept the invitation before you can sign account recovery request.
          `,
                      data: {
                        event: "recovery_initiated",
                        message: `${creatorId} account recovery has started,  but it’s not complete yet!.
          You are required to help sign the account recovery to complete it.

          Please Note: You are yet to accept the cosigner invitation, You have to accept the invitation before you can sign account recovery request.
          `,
                      },
                    },
                  },
                });
              }
            });
          }
        }
      });
      await Promise.all(alertCosignersInvPromises);
    }

    const _authenticated = await authenticateFirestore(userId, vaultPinHashed);
    if (_authenticated) {
      return await axios({
        url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
        method: "POST",

        data: {
          uniqueId: _authenticated,
          type: "generate",
        },
      }).then(async (res) => {
        if (res?.data?.token) {
          return await axios({
            url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
            method: "POST",
            headers: {
              Authorization: `Bearer ${res?.data?.token}`,
            },
            data: {
              recipient: creatorId,
              type: "recovery_initiated",
              subject: "Verified Wallet Co-signers",
              vaultId: creatorId,
              vaultPinHashed: vaultPinHashed,
              txId: String(txId),
              origin: defaultVerifiedUrl,
              requiredSigners: String(requiredSigners),
              notification: {
                userId: userId,
                title: "🔐 Account Recovery Started!",
                body: `Your account recovery has started,  but it’s not complete yet!
          Here’s what must happen next:
          1. ${
            Number(requiredSigners) - 1
          } of your co-signers must sign off on your recovery request.
          2. Then, you’ll need to sign it yourself to finish the process.
          3. Only after these steps can your account be fully recovered`,
                data: {
                  event: "recovery_initiated",

                  message: `Your account recovery has started,  but it’s not complete yet!
          Here’s what must happen next:
          1. ${
            Number(requiredSigners) - 1
          } of your co-signers must sign off on your recovery request.
          2. Then, you’ll need to sign it yourself to finish the process.
          3. Only after these steps can your account be fully recovered`,
                },
              },
            },
          })
            .then((res) => {
              if (res?.data) {
                return true;
              } else {
                return false;
              }
            })
            .catch((err) => {
              if (process.env.REACT_APP_NODE_ENV !== "production") {
                console.error(
                  "coSinerInvitation failed with error: ",
                  err?.message,
                );
              }

              return false;
            });
        } else {
          return false;
        }
      });
    }
  } else {
    return false;
  }
};

export const sendCreatorSigned = async (
  channel,
  creatorId,
  coSignerId,
  vaultPinHashed,
) => {
  const userId = hashTheString(coSignerId);
  const authenticated = await authenticateFirestore(userId, vaultPinHashed);
  if (authenticated) {
    await getAndSaveToken(userId);
    return await axios({
      url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
      method: "POST",

      data: {
        uniqueId: authenticated,
        type: "generate",
      },
    })
      .then(async (res) => {
        if (res?.data?.token) {
          return await axios({
            url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
            method: "POST",

            headers: {
              Authorization: `Bearer ${res?.data?.token}`,
            },
            data: {
              recipient: creatorId,
              type: "recovery_signed",
              subject: "Verified Wallet Account Recovery",
              coSignerId: coSignerId,
              origin: defaultVerifiedUrl,
              vaultId: coSignerId,
              notification: {
                userId: userId,
                title: " ✍️ Account Recovery Signed!",
                body: `Your account recovery has been approved by user: ${coSignerId}`,
                data: {
                  event: "recovery_signed",
                  message: `Your account recovery has been approved by user: ${coSignerId}`,
                },
              },
            },
          })
            .then((res) => {
              if (res?.data) {
                return true;
              } else {
                return false;
              }
            })
            .catch((err) => {
              if (process.env.REACT_APP_NODE_ENV !== "production") {
                console.error(
                  "coSinerInvitation failed with error: ",
                  err?.message,
                );
              }

              return false;
            });
        } else {
          return false;
        }
      })
      .catch((err) => {
        if (process.env.REACT_APP_NODE_ENV !== "production") {
          console.error("coSinerInvitation failed with error: ", err?.message);
        }

        return false;
      });
  }
};

export const sendCreatorCompleted = async (
  channel,
  creatorId,
  coSignerId,
  vaultPinHashed,
  txId,
) => {
  const userId = hashTheString(coSignerId);
  const authenticated = await authenticateFirestore(userId, vaultPinHashed);
  if (authenticated) {
    await getAndSaveToken(userId);
    return await axios({
      url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
      method: "POST",

      data: {
        uniqueId: authenticated,
        type: "generate",
      },
    })
      .then(async (res) => {
        if (res?.data?.token) {
          return await axios({
            url: channel === "sms" ? smsSenderUrl : emailSenderUrl,
            method: "POST",
            headers: {
              Authorization: `Bearer ${res?.data?.token}`,
            },
            data: {
              recipient: creatorId,
              type: "recovery_completed",
              subject: "Verified Wallet Account Recovery",
              txId: String(txId),
              vaultId: creatorId,
              origin: defaultVerifiedUrl,
              notification: {
                userId: userId,
                title: "🎉 Account Recovery Signed!",
                body: `Your account recovery is complete.`,
                data: {
                  event: "recovery_completed",
                  message: `Your account recovery is complete.`,
                },
              },
            },
          })
            .then((res) => {
              if (res?.data) {
                return true;
              } else {
                return false;
              }
            })
            .catch((err) => {
              if (process.env.REACT_APP_NODE_ENV !== "production") {
                console.error(
                  "coSinerInvitation failed with error: ",
                  err?.message,
                );
              }

              return false;
            });
        } else {
          return false;
        }
      })
      .catch((err) => {
        if (process.env.REACT_APP_NODE_ENV !== "production") {
          console.error("coSinerInvitation failed with error: ", err?.message);
        }

        return false;
      });
  }
};

const convertFromBlockTimestamp = (blockTimestamp) => {
  const unixTime = new Date(Number(blockTimestamp) * 1000);
  return unixTime.toLocaleString("default", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

export const fetchWalletActivities = async (
  userAdress,
  apiKey,
  chainExplorerUrl,
  chainId,
  rpcUrl,
  nativeTokenSymbol,
  currentBlock,
) => {
  try {
    const normalTxs = await axios.get(chainExplorerUrl, {
      params: {
        chainid: chainId,
        module: "account",
        action: "txlist",
        address: userAdress,
        startblock: 0,
        endBlock: currentBlock || 999999999,
        sort: "desc",
        apikey: apiKey,
      },
    });

    const tokensTxs = await axios.get(chainExplorerUrl, {
      params: {
        chainid: chainId,
        module: "account",
        action: "tokentx",
        address: userAdress,
        startblock: 0,
        endBlock: currentBlock || 999999999,
        sort: "desc",
        apikey: apiKey,
      },
    });

    const allTxs = [];

    for (const tx of normalTxs?.data?.result) {
      if (Number(tx?.timeStamp) > 0) {
        allTxs.push({
          type:
            tx?.to === ""
              ? "Contract Creation"
              : tx?.input !== "0x"
                ? "Contract Interaction"
                : `${nativeTokenSymbol} Transfer`,
          value: parseFloat(tx?.value) / 1e18,
          hash: tx?.hash,
          from: tx?.from,
          to: tx?.to,
          timeStamp: Number(tx?.timeStamp),
          date: convertFromBlockTimestamp(Number(tx?.timeStamp)),
          owner: userAdress,
        });
      }
    }
    const allTkns = [];
    for (const tx of tokensTxs?.data?.result) {
      const isSender = tx?.from?.toLowerCase() === userAdress?.toLowerCase();
      if (tx?.tokenSymbol?.length > 0 && Number(tx?.timeStamp) > 0) {
        allTxs.push({
          type: isSender
            ? `Send ${tx?.tokenSymbol}`
            : `Receive ${tx?.tokenSymbol}`,
          value: parseFloat(tx?.value) / 1e18,
          timeStamp: Number(tx?.timeStamp),
          date: convertFromBlockTimestamp(Number(tx?.timeStamp)),
          hash: tx?.hash,
          from: tx?.from,
          to: tx?.to,
          owner: userAdress,
        });
      }

      if (
        !allTkns?.find(
          (tk) =>
            tk?.address?.toLowerCase() === tx?.contractAddress?.toLowerCase(),
        ) &&
        tx?.contractAddress?.length > 0
      ) {
        const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
        const erc20Abi = [
          "function name() view returns (string)",
          "function symbol() view returns (string)",
          "function decimals() view returns (uint8)",
          "function balanceOf(address owner) view returns (uint256)",
        ];

        const contract = new ethers.Contract(
          tx?.contractAddress,
          erc20Abi,
          provider,
        );

        const name = await contract.name();
        const symbol = await contract.symbol();
        const decimals = await contract.decimals();
        const balance = await contract.balanceOf(userAdress);

        allTkns.push({
          name,
          symbol,
          decimals,
          balance: Number(balance) / 10 ** Number(decimals),
          address: tx?.contractAddress,
          owner: userAdress,
        });
      }
    }

    const provider = new ethers.providers.JsonRpcProvider(rpcUrl);

    const nativeBalance = await provider.getBalance(userAdress);

    const allPaymentTokens = [];

    for (const token of chainDetails[Number(chainId)]?.paymentTokens || []) {
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
      const erc20Abi = [
        "function name() view returns (string)",
        "function symbol() view returns (string)",
        "function decimals() view returns (uint8)",
        "function balanceOf(address owner) view returns (uint256)",
      ];

      const contract = new ethers.Contract(token, erc20Abi, provider);

      const name = await contract.name();
      const symbol = await contract.symbol();
      const decimals = await contract.decimals();
      const balance = await contract.balanceOf(userAdress);

      // console.log("Tokens: ", name, symbol, decimals, balance);

      allPaymentTokens.push({
        name,
        symbol,
        decimals,
        blanceRaw: balance,
        balance: Number(balance) / 10 ** Number(decimals),
        address: token,
      });
    }

    return {
      transactions: allTxs,
      tokens: allTkns,
      ethBalance: nativeBalance,
      ethBalanceFmt: ethers.utils.formatEther(nativeBalance),
      paymentTokens: allPaymentTokens,
    };
  } catch (err) {
    console.error("Error fetch transactions list: ", err);
    return {
      transactions: [],
      tokens: [],
      ethBalance: "0.00",
      ethBalanceFmt: "0.00",
      paymentTokens: [],
    };
  }
};
